import { api } from '../../libs/api'

const initialState = () => ({
  target: 'job',
  latestId: null,
  status: null,
  filename: null,
  errorMessage: null,
  pollTimer: null,
})

const state = initialState()

const getters = {
  target: st => st.target,
  latestId: st => st.latestId,
  status: st => st.status,
  filename: st => st.filename,
  errorMessage: st => st.errorMessage,

  isUploading: st => st.status === 'uploading',
  isUploaded: st => st.status === 'uploaded',
  isFailed: st => st.status === 'failed',
}

const normalizeTarget = t => (
  ['job', 'job_list', 'company', 'company_list'].includes(t) ? t : 'job'
)

const pickLatest = (data) => {
  if (!data) return null
  if (data.results && data.results[0]) return data.results[0]
  if (data.id) return data
  return null
}

const applyState = (st, next) => {
  Object.keys(next).forEach((k) => {
    // eslint-disable-next-line no-param-reassign
    st[k] = next[k]
  })
}

const mutations = {
  setTarget (st, target) {
    const t = normalizeTarget(target || 'job')

    if (st.pollTimer) clearInterval(st.pollTimer)

    const next = {
      target: t,
      latestId: null,
      status: null,
      filename: null,
      errorMessage: null,
      pollTimer: null,
    }
    applyState(st, next)
  },

  setArchive (st, payload) {
    const next = {
      target: (payload && payload.target) ? payload.target : st.target,
      latestId: (payload && payload.id !== undefined && payload.id !== null) ? payload.id : st.latestId,
      status: (payload && payload.status) ? payload.status : null,
      filename: (payload && payload.filename) ? payload.filename : null,
      errorMessage: (payload && payload.error_message) ? payload.error_message : null,
      pollTimer: st.pollTimer,
    }
    applyState(st, next)
  },

  setStatus (st, payload) {
    const next = {
      target: (payload && payload.target) ? payload.target : st.target,
      latestId: (payload && payload.id !== undefined && payload.id !== null) ? payload.id : st.latestId,
      status: (payload && payload.status) ? payload.status : st.status,
      filename: (payload && payload.filename) ? payload.filename : st.filename,
      errorMessage: (payload && payload.error_message) ? payload.error_message : null,
      pollTimer: st.pollTimer,
    }
    applyState(st, next)
  },

  clearArchive (st) {
    const next = {
      target: st.target,
      latestId: null,
      status: null,
      filename: null,
      errorMessage: null,
      pollTimer: st.pollTimer,
    }
    applyState(st, next)
  },

  clearWatch (st) {
    if (st.pollTimer) clearInterval(st.pollTimer)
    const next = {
      target: st.target,
      latestId: st.latestId,
      status: st.status,
      filename: st.filename,
      errorMessage: st.errorMessage,
      pollTimer: null,
    }
    applyState(st, next)
  },

  setPollTimer (st, t) {
    const next = {
      target: st.target,
      latestId: st.latestId,
      status: st.status,
      filename: st.filename,
      errorMessage: st.errorMessage,
      pollTimer: t,
    }
    applyState(st, next)
  },
}

const actions = {
  setTarget ({ commit }, target) {
    commit('setTarget', target)
  },

  async create ({ commit, dispatch, state: st }, payload) {
    const t = normalizeTarget((payload && payload.target) || st.target)

    // ★ページ遷移時の誤表示を防ぐ：先にtarget切替＆クリア
    commit('setTarget', t)

    const data = await api.createUploadArchive(payload)
    const archive = pickLatest(data)
    if (archive) commit('setArchive', archive)

    dispatch('startWatch', { target: t })
    return archive
  },

  async createSearchJob ({ commit, dispatch }, payload) {
    commit('setTarget', 'job')

    const data = await api.createSearchJobUploadArchive(payload)
    const archive = pickLatest(data)
    if (archive) commit('setArchive', archive)

    dispatch('startWatch', { target: 'job' })
    return { archive, meta: (data && data.meta) || {} }
  },

  async fetchLatest ({ commit, state: st }, { target } = {}) {
    const t = normalizeTarget(target || st.target)

    // ★先にクリア（前画面の uploaded を残さない）
    commit('setTarget', t)

    const data = await api.fetchUploadArchives({ target: t })
    const latest = pickLatest(data)

    if (latest) commit('setArchive', latest)
    else commit('clearArchive')

    return latest
  },

  startWatch ({ commit, dispatch, state: st }, { target } = {}) {
    const t = normalizeTarget(target || st.target)

    if (st.pollTimer) return

    if (t !== st.target) commit('setTarget', t)
    commit('clearWatch')
    dispatch('poll', { target: t })
  },

  poll ({ commit, state: st, dispatch }, { target } = {}) {
    if (st.pollTimer) return
    const t = normalizeTarget(target || st.target)

    const timer = setInterval(async () => {
      try {
        const data = await api.fetchUploadArchives({ target: t })
        const latest = pickLatest(data)
        if (!latest) return

        if (st.target !== t) return
        commit('setStatus', latest)

        if (latest.status === 'failed') {
          dispatch(
            'common/pushToastError',
            { error: new Error('データ作成に失敗しました') },
            { root: true },
          )
        }

        if (['uploaded', 'downloaded', 'failed'].includes(latest.status)) {
          clearInterval(timer)
          commit('setPollTimer', null)
        }
      } catch (e) {
        // polling中の一時的エラーは無視
      }
    }, 3000)

    commit('setPollTimer', timer)
  },

  async download ({ dispatch, state: st }, { target, id } = {}) {
    const t = normalizeTarget(target || st.target)
    let errorMessage = null

    try {
      let archiveId = id || st.latestId
      if (!archiveId) {
        const data = await api.fetchUploadArchives({ target: t })
        const latest = pickLatest(data)
        archiveId = latest && latest.id
      }
      if (!archiveId) throw new Error('ダウンロード可能なファイルがありません')

      const res = await api.getUploadArchiveDownloadUrl(archiveId, { target: t })
      const url = res && res.url
      if (!url) throw new Error('ダウンロードURLの取得に失敗しました')

      window.location.href = url

      try {
        await api.confirmUploadArchiveDownload(archiveId, { target: t })
        dispatch('fetchLatest', { target: t })
      } catch (e) {
        if (e && e.response && e.response.data && e.response.data.error) errorMessage = e.response.data.error
        else if (e && e.message) errorMessage = e.message
        else errorMessage = 'ダウンロード完了処理に失敗しました'
      }
    } catch (e) {
      if (!errorMessage) {
        if (e && e.response && e.response.data && e.response.data.error) errorMessage = e.response.data.error
        else if (e && e.message) errorMessage = e.message
        else errorMessage = 'ダウンロードに失敗しました'
      }
    }

    if (errorMessage) throw new Error(errorMessage)
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}



// WEBPACK FOOTER //
// ./src/store/modules/uploadArchive.js